import { Link, useParams } from "@tanstack/react-router";
import { LuTriangleAlert, LuExternalLink, LuGraduationCap, LuMail, LuUserX } from "react-icons/lu";
import { useQuery } from "@/lib/convex";
import { groupLink } from "@/lib/groupLink";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  Grid,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Stack,
  Text,
} from "@chakra-ui/react";
import type { Id } from "@convex/_generated/dataModel";
import { api } from "@convex/_generated/api";
import type { FunctionReturnType } from "convex/server";
import { PageHeader } from "../components/PageHeader";
import { useEffectivePersonId } from "../impersonation";

// Derived from the queries, never hand-written: a local shape plus an `as` cast
// is how a backend change reaches the browser instead of the build.
type PublicProfileView = NonNullable<
  FunctionReturnType<typeof api.persons.getPersonProfileByShortName>
>;
// The two queries wrap the same person payload differently -- the viewer's
// carries an effective account for impersonation, the public one does not -- so
// each page normalises into this rather than the component juggling a union.
// The variant that actually carries a person; the missing-person variant has
// person: null and empty arrays, which would widen every field into a union.
type LinkedProfile = Extract<PublicProfileView, { person: object }>;
type PersonProfileView = {
  person: LinkedProfile["person"];
  accounts: LinkedProfile["accounts"];
  enrollments: LinkedProfile["enrollments"];
  affiliations: LinkedProfile["affiliations"];
  roleLabels: readonly string[];
};
type AffiliationView = PersonProfileView["affiliations"][number];

type BadgeTone = "success" | "warning" | "neutral" | "error";

function StatusBadge({ label, tone }: { label: string; tone: BadgeTone }) {
  const colorPalette = {
    success: "green",
    warning: "orange",
    neutral: "gray",
    error: "red",
  }[tone];

  return (
    <Badge colorPalette={colorPalette} variant={tone === "neutral" ? "subtle" : "solid"} textTransform="uppercase" letterSpacing="0.18em">
      {label}
    </Badge>
  );
}

function LoadingCard({ label }: { label: string }) {
  return (
    <Card.Root borderWidth="1px" borderColor="border">
      <Card.Body p={6}>
        <Text fontSize="sm" color="fg.muted">{label}</Text>
      </Card.Body>
    </Card.Root>
  );
}

function NotFoundCard({ shortName }: { shortName: string }) {
  return (
    <Card.Root borderWidth="1px" borderStyle="dashed" borderColor="border">
      <Card.Body gap={3} p={8} textAlign="center">
        <Heading size="xl">No CeMMie found</Heading>
        <Text fontSize="sm" color="fg.muted">
          No user profile with the short name <Text as="span" fontWeight="semibold" color="fg">{shortName}</Text> exists in CeMMbase.
        </Text>
        <Box>
          <Button asChild colorPalette="blue">
            <Link to="/cemmies">Back to CeMMies</Link>
          </Button>
        </Box>
      </Card.Body>
    </Card.Root>
  );
}

function MissingPersonCard() {
  return (
    <Card.Root borderWidth="1px" borderColor="red.300">
      <Card.Body gap={4} p={8}>
        <HStack gap={3} color="red.500">
          <Icon as={LuTriangleAlert} boxSize={6} />
          <Heading size="xl">This record is incomplete</Heading>
        </HStack>
        <Text maxW="2xl" fontSize="sm" color="fg.muted">
          Something is wrong with this person's record. Please tell a system admin.
        </Text>
      </Card.Body>
    </Card.Root>
  );
}

function UnlinkedProfileCard({ profile }: { profile: { auth: { tokenIdentifier: string; email: string | null } } }) {
  return (
    <Card.Root borderWidth="1px" borderColor="orange.300">
      <Card.Body gap={4} p={8}>
        <HStack gap={3} color="orange.500">
          <Icon as={LuUserX} boxSize={6} />
          <Heading size="xl">You are not in CeMMbase yet</Heading>
        </HStack>
        <Text maxW="2xl" fontSize="sm" color="fg.muted">
          You are signed in, but CeMMbase has no record for you yet. Please contact IT.
        </Text>
        <SimpleGrid gap={4} columns={{ base: 1, sm: 2 }}>
          <Box borderWidth="1px" borderColor="border" px={4} py={4}>
            <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color="fg.muted">Auth identity</Text>
            <Text mt={2} fontSize="sm" color="fg.muted">{profile.auth.tokenIdentifier}</Text>
          </Box>
          <Box borderWidth="1px" borderColor="border" px={4} py={4}>
            <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color="fg.muted">Email</Text>
            <Text mt={2} fontSize="sm" color="fg.muted">{profile.auth.email ?? "—"}</Text>
          </Box>
        </SimpleGrid>
      </Card.Body>
    </Card.Root>
  );
}

/**
 * Avatar and status tags for the page header.
 *
 * The identity card that used to sit above the content said nothing the header
 * did not: a name, a picture, and a few badges. Folding it into the header bar
 * gives the details and the affiliations the full width instead.
 */
function ProfileAvatar({ person }: { person: PersonProfileView["person"] }) {
  const initials = `${person.firstName[0] ?? ""}${person.lastName[0] ?? ""}`.toUpperCase();
  return (
    <Avatar.Root size="2xl">
      <Avatar.Fallback name={initials || "CU"} />
      {person.profileImageUrl ? (
        <Avatar.Image src={person.profileImageUrl} alt={`${person.fullName} profile`} />
      ) : null}
    </Avatar.Root>
  );
}

function ProfileTags({ profile }: { profile: PersonProfileView }) {
  const enrollment = profile.enrollments[0] ?? null;
  const currentAccount = profile.accounts.find((account) => account.removedFromEntraAt === null);
  return (
    <HStack flexWrap="wrap" justify="flex-end" gap={2}>
      {/* Derived from affiliations, not a stored flag: a CeMMie, an alumnus
          still on a wrap-up agreement, or someone starting. */}
      <StatusBadge label={profile.person.statusLabel} tone={profile.person.isCeMMie ? "success" : "neutral"} />
      {profile.roleLabels.map((roleLabel) => (
        <StatusBadge key={roleLabel} label={roleLabel} tone="warning" />
      ))}
      {enrollment?.cohort ? <StatusBadge label={`Cohort ${enrollment.cohort}`} tone="success" /> : null}
      {currentAccount ? null : <StatusBadge label="No current account" tone="neutral" />}
    </HStack>
  );
}

function PersonLink({ person }: { person: { fullName: string; shortName: string | null } }) {
  return person.shortName ? (
    <Link to="/cemmies/$shortName" params={{ shortName: person.shortName }}>
      <Text as="span" _hover={{ textDecoration: "underline" }}>{person.fullName}</Text>
    </Link>
  ) : (
    <Text as="span">{person.fullName}</Text>
  );
}

/** The student's current co-supervisor, inside the enrollment card. */
function CoSupervisorTile({ studentPersonId }: { studentPersonId: Id<"persons"> }) {
  const effectivePersonId = useEffectivePersonId() as Id<"persons"> | null;
  const record = useQuery(api.coSupervision.forStudent, { effectivePersonId, studentPersonId });
  const current = record?.current?.coSupervisor ?? null;
  return (
    <Box borderWidth="1px" borderColor="border" px={4} py={4} gridColumn={{ sm: "span 2" }}>
      <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color="fg.muted">Co-supervisor</Text>
      <Text mt={2}>{current ? <PersonLink person={current} /> : record === undefined ? "…" : "None recorded"}</Text>
    </Box>
  );
}

/** Whom this person co-supervises. Shown only when there is somebody. */
function CoSupervisesCard({ personId }: { personId: Id<"persons"> }) {
  const effectivePersonId = useEffectivePersonId() as Id<"persons"> | null;
  const students = useQuery(api.coSupervision.forCoSupervisor, { effectivePersonId, coSupervisorPersonId: personId });
  if (!students || students.length === 0) {
    return null;
  }
  return (
    <Card.Root borderWidth="1px" borderColor="border">
      <Card.Body gap={3} p={6}>
        <HStack gap={3}>
          <Icon as={LuGraduationCap} boxSize={5} color="blue.500" />
          <Heading size="lg">Co-supervises</Heading>
        </HStack>
        <Stack gap={1}>
          {students.map((student) => (
            <Text key={student.personId}><PersonLink person={student} /></Text>
          ))}
        </Stack>
      </Card.Body>
    </Card.Root>
  );
}

function ProfileContent({ profile, mode }: { profile: PersonProfileView; mode: "viewer" | "public" }) {
  const person = profile.person;
  const currentAffiliations = profile.affiliations.filter((affiliation) => affiliation.isCurrent);
  const pastAffiliations = profile.affiliations.filter((affiliation) => !affiliation.isCurrent);
  const enrollment = profile.enrollments[0] ?? null;
  // Past accounts are retained, so the short names and UPNs of people who have
  // left stay resolvable.
  const currentAccount = profile.accounts.find((account) => account.removedFromEntraAt === null);

  return (
    <Grid gap={6} templateColumns={{ base: "1fr", xl: "1fr 1.2fr" }} alignItems="start">
      <Stack gap={6}>
        <Card.Root borderWidth="1px" borderColor="border">
          <Card.Body gap={6} p={{ base: 6, sm: 8 }}>
            <Heading size="xl">Person details</Heading>

            <SimpleGrid gap={5} columns={{ base: 1, sm: 2 }}>
              <Box borderWidth="1px" borderColor="border" px={4} py={4}>
                <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color="fg.muted">First name</Text>
                <Text mt={2}>{person.firstName}</Text>
              </Box>
              <Box borderWidth="1px" borderColor="border" px={4} py={4}>
                <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color="fg.muted">Last name</Text>
                <Text mt={2}>{person.lastName}</Text>
              </Box>
              <Box borderWidth="1px" borderColor="border" px={4} py={4} gridColumn={{ sm: "span 2" }}>
                <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color="fg.muted">Email</Text>
                <HStack mt={2} gap={2}>
                  <Icon as={LuMail} boxSize={4} color="fg.muted" />
                  <Text>{person.email ?? "—"}</Text>
                </HStack>
              </Box>
              {currentAccount ? (
                <Box borderWidth="1px" borderColor="border" px={4} py={4} gridColumn={{ sm: "span 2" }}>
                  <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color="fg.muted">Account</Text>
                  <Text mt={2}>{currentAccount.shortName}</Text>
                  <Text fontSize="xs" color="fg.muted">
                    {currentAccount.userPrincipalName}
                    {currentAccount.userType === "guest" ? " · guest account" : ""}
                  </Text>
                </Box>
              ) : null}
            </SimpleGrid>
          </Card.Body>
        </Card.Root>

      </Stack>

      <Stack gap={6}>
        {enrollment ? (
          <Card.Root borderWidth="1px" borderColor="border">
            <Card.Body gap={4} p={6}>
              <HStack gap={3}>
                <Icon as={LuGraduationCap} boxSize={5} color="blue.500" />
                <Heading size="lg">Programme enrollment</Heading>
              </HStack>
              <SimpleGrid gap={4} columns={{ base: 1, sm: 2 }}>
                <Box borderWidth="1px" borderColor="border" px={4} py={4}>
                  <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color="fg.muted">Cohort</Text>
                  <Text mt={2}>{enrollment.cohort ?? "—"}</Text>
                </Box>
                <Box borderWidth="1px" borderColor="border" px={4} py={4}>
                  <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color="fg.muted">Status</Text>
                  <Text mt={2}>{enrollment.status}</Text>
                </Box>
                {enrollment.defenceDate ? (
                  <Box borderWidth="1px" borderColor="border" px={4} py={4} gridColumn={{ sm: "span 2" }}>
                    {/* Frequently after the affiliation has ended -- which is
                        why enrollment is independent of employment. */}
                    <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color="fg.muted">Defence</Text>
                    <Text mt={2}>{enrollment.defenceDate}</Text>
                  </Box>
                ) : null}
                <CoSupervisorTile studentPersonId={person._id} />
              </SimpleGrid>
            </Card.Body>
          </Card.Root>
        ) : null}

        <CoSupervisesCard personId={person._id} />

        <AffiliationsCard affiliations={profile.affiliations} currentAffiliations={currentAffiliations} pastAffiliations={pastAffiliations} />
      </Stack>
    </Grid>
  );
}

function AffiliationsCard({ affiliations, currentAffiliations, pastAffiliations }: { affiliations: AffiliationView[]; currentAffiliations: AffiliationView[]; pastAffiliations: AffiliationView[] }) {
  return (
    <Card.Root borderWidth="1px" borderColor="border">
      <Card.Body gap={6} p={{ base: 6, sm: 8 }}>
        <Box>
          <Heading size="xl">Groups</Heading>
          <Text mt={2} fontSize="sm" color="fg.muted">
            Current and past groups.
          </Text>
        </Box>

        {affiliations.length === 0 ? (
          <Box borderWidth="1px" borderStyle="dashed" borderColor="border" px={5} py={6}>
            <Text fontSize="sm" color="fg.muted">No affiliations are assigned to this person yet.</Text>
          </Box>
        ) : (
          <Stack gap={6}>
            <AffiliationSection title="Current affiliations" affiliations={currentAffiliations} emptyLabel="No current affiliations." tone="active" />
            <AffiliationSection title="Past affiliations" affiliations={pastAffiliations} emptyLabel="No historical affiliations." tone="past" />
          </Stack>
        )}
      </Card.Body>
    </Card.Root>
  );
}

function AffiliationSection({ title, affiliations, emptyLabel, tone }: { title: string; affiliations: AffiliationView[]; emptyLabel: string; tone: "active" | "past" }) {
  return (
    <Stack gap={3}>
      <Text fontSize="xs" fontWeight="semibold" textTransform="uppercase" letterSpacing="0.18em" color="fg.muted">{title}</Text>
      {affiliations.length === 0 ? (
        <Box borderWidth="1px" borderStyle="dashed" borderColor="border" px={5} py={4}>
          <Text fontSize="sm" color="fg.muted">{emptyLabel}</Text>
        </Box>
      ) : (
        affiliations.map((affiliation) => (
          <Box key={affiliation._id} borderWidth="1px" borderColor="border" px={5} py={5}>
            <Stack direction={{ base: "column", sm: "row" }} align={{ sm: "flex-start" }} justify="space-between" gap={3}>
              <Box>
                <HStack flexWrap="wrap" gap={2}>
                  {affiliation.orgUnit ? (
                    <Link to="/groups/$shortName" {...groupLink(affiliation.orgUnit)}>
                      <Heading size="md" _hover={{ textDecoration: "underline" }}>{affiliation.orgUnit.name}</Heading>
                    </Link>
                  ) : (
                    <Heading size="md">Unknown unit</Heading>
                  )}
                  {affiliation.isPrimary ? (
                    // The primary unit decides who supervises this person.
                    <StatusBadge label="Primary" tone="warning" />
                  ) : null}
                </HStack>
                <Text mt={1} fontSize="sm" color="fg.muted">{affiliation.positionLabel}</Text>
                <Text mt={2} fontSize="xs" color="fg.muted">
                  {affiliation.basisLabel}
                  {affiliation.countsAsCeMMie ? "" : " · outside the CeMMie headcount"}
                </Text>
                <Text mt={1} fontSize="xs" color="fg.muted">
                  {/* Start dates are the import's estimate until HR's
                      come-and-go lists become a usable input. */}
                  {affiliation.startDateIsEstimated ? "from about " : "from "}
                  {affiliation.startDate}
                  {affiliation.endDate ? ` to ${affiliation.endDate}` : ""}
                </Text>
                {affiliation.orgUnit ? (
                  <Text mt={2} fontSize="xs" textTransform="uppercase" letterSpacing="0.18em" color="fg.muted">
                    {affiliation.orgUnit.typeLabel ?? "Unknown type"}
                    {affiliation.orgUnit.isClosed ? " · closed unit" : ""}
                  </Text>
                ) : null}
              </Box>
              <StatusBadge label={affiliation.roleLabel} tone={tone === "active" && affiliation.headRole !== null ? "warning" : tone === "active" ? "success" : "neutral"} />
            </Stack>
            {affiliation.orgUnit?.description ? (
              <Text mt={3} fontSize="sm" color="fg.muted">{affiliation.orgUnit.description}</Text>
            ) : null}
            {affiliation.orgUnit?.url ? (
              <a href={affiliation.orgUnit.url} target="_blank" rel="noreferrer">
                <HStack mt={3} gap={2} fontSize="sm" fontWeight="medium" color="blue.500" _hover={{ textDecoration: "underline" }}>
                  Visit unit page
                  <Icon as={LuExternalLink} boxSize={4} />
                </HStack>
              </a>
            ) : null}
          </Box>
        ))
      )}
    </Stack>
  );
}

export function ProfilePage() {
  const effectivePersonId = useEffectivePersonId();
  const profile = useQuery(api.persons.viewerProfile, {
    effectivePersonId: effectivePersonId as Id<"persons"> | null,
  });

  // Normalised once, so the header and the body see the same thing.
  const viewerPerson: PersonProfileView | null =
    profile && profile.status !== "unlinked" && profile.person !== null
      ? {
          person: profile.person,
          accounts: profile.accounts,
          enrollments: profile.enrollments,
          affiliations: profile.affiliations,
          // The effective account, so an impersonated view shows that person's
          // roles rather than the real viewer's.
          roleLabels: profile.effectiveAccount?.assignedRoleLabels ?? [],
        }
      : null;

  const renderContent = () => {
    if (profile === undefined) {
      return <LoadingCard label="Loading profile…" />;
    }

    if (profile === null) {
      return (
        <Card.Root borderWidth="1px" borderColor="border">
          <Card.Body gap={3} p={8} textAlign="center">
            <Heading size="xl">Not authenticated</Heading>
            <Text fontSize="sm" color="fg.muted">
              Sign in with Microsoft Entra ID to load your CeMMbase profile.
            </Text>
          </Card.Body>
        </Card.Root>
      );
    }

    if (profile.status === "unlinked") {
      return <UnlinkedProfileCard profile={profile} />;
    }

    if (viewerPerson === null) {
      return <MissingPersonCard />;
    }

    return <ProfileContent profile={viewerPerson} mode="viewer" />;
  };

  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title={viewerPerson ? viewerPerson.person.fullName : "Profile"}
        description="Your details, roles and groups at CeMM."
        loading={profile === undefined}
        breadcrumbs={[{ label: "CeMMbase", to: "/" }, { label: "Profile" }]}
        leading={viewerPerson ? <ProfileAvatar person={viewerPerson.person} /> : undefined}
        actions={viewerPerson ? <ProfileTags profile={viewerPerson} /> : undefined}
      />

      <Box px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>{renderContent()}</Box>
    </Box>
  );
}

export function CeMMieProfilePage() {
  const params = useParams({ from: "/_authed/cemmies/$shortName", structuralSharing: true });
  const profile = useQuery(api.persons.getPersonProfileByShortName, {
    shortName: params.shortName,
  });

  const person: PersonProfileView | null =
    profile && profile.person !== null
      ? {
          person: profile.person,
          accounts: profile.accounts,
          enrollments: profile.enrollments,
          affiliations: profile.affiliations,
          roleLabels: profile.account.assignedRoleLabels,
        }
      : null;

  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title={person?.person.fullName ?? params.shortName}
        leading={person ? <ProfileAvatar person={person.person} /> : undefined}
        actions={person ? <ProfileTags profile={person} /> : undefined}
        description="Their details and groups at CeMM."
        loading={profile === undefined}
        breadcrumbs={[{ label: "CeMMbase", to: "/" }, { label: "CeMMies", to: "/cemmies" }, { label: profile?.person?.fullName ?? params.shortName }]}
      />

      <Box px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>
        {profile === undefined ? (
          <LoadingCard label="Loading CeMMie profile…" />
        ) : profile === null ? (
          <NotFoundCard shortName={params.shortName} />
        ) : person === null ? (
          <MissingPersonCard />
        ) : (
          <ProfileContent profile={person} mode="public" />
        )}
      </Box>
    </Box>
  );
}
